import type { DropdownDeclaration, VariableDeclaration } from "@components/promptVariables";
import { DOCUMENT_CATEGORIES, DRAWINGS_CATEGORY } from "@constants/documentCategories";
import type { CustomPromptGetResponse, Prompt } from "./types";

/**
 * Legacy persisted shape: dropdown declaration with a flat `options` array
 * that may embed `&Foo` builtin/category tokens. No `optionType` discriminator.
 */
interface StoredDropdownLegacy {
  displayName: string;
  varName: string;
  helpText: string | null;
  type: "dropdown" | "dropdown-multi";
  options: string[];
}

type StoredDeclaration = VariableDeclaration | StoredDropdownLegacy;

export const customPromptsQueryKey = () => ["customPrompts"] as const;

const LEGACY_PREFIX = "&";
const LEGACY_DIAGRAMS_TOKEN = "diagrams";
const LEGACY_DOCUMENTS_TOKEN = "documents";
const ALL_CATEGORY_NAMES: readonly string[] = [...new Set(Object.values(DOCUMENT_CATEGORIES).flat())];

/**
 * One-shot translation of legacy `&Foo`-style declarations into the new
 * discriminated-union shape. New-shape inputs pass through unchanged.
 */
export const transformLegacyDeclarations = (declarations: StoredDeclaration[]): VariableDeclaration[] =>
  declarations.map(migrateOne);

const migrateOne = (decl: StoredDeclaration): VariableDeclaration => {
  if (decl.type === "text" || decl.type === "textarea") return decl;

  if ("optionType" in decl) return decl;

  return transformLegacyDropdown(decl);
};

const transformLegacyDropdown = (decl: StoredDropdownLegacy): DropdownDeclaration => {
  const base = { displayName: decl.displayName, varName: decl.varName, type: decl.type, helpText: decl.helpText };
  const { options } = decl;
  const legacyToken = options.find((o) => typeof o === "string" && o.startsWith(LEGACY_PREFIX) && o.length > 1);

  if (!legacyToken) return { ...base, optionType: "custom", options };

  const nameLower = legacyToken.slice(LEGACY_PREFIX.length).toLowerCase();

  if (nameLower === LEGACY_DIAGRAMS_TOKEN) return { ...base, optionType: "category", category: DRAWINGS_CATEGORY };
  if (nameLower === LEGACY_DOCUMENTS_TOKEN) return { ...base, optionType: "custom", options: [] };

  const matchedCategory = ALL_CATEGORY_NAMES.find((c) => c.toLowerCase() === nameLower);
  if (matchedCategory) return { ...base, optionType: "category", category: matchedCategory };

  return { ...base, optionType: "custom", options: [] };
};

export const normalizeUserPrompts = (raw: CustomPromptGetResponse): CustomPromptGetResponse => ({
  ...raw,
  userPrompts: {
    ...raw.userPrompts,
    prompts: normalizePrompts(raw.userPrompts.prompts),
  },
});

const normalizePrompts = (prompts: Record<string, Prompt>): Record<string, Prompt> =>
  Object.fromEntries(Object.entries(prompts).map(([key, prompt]) => [key, normalizePrompt(prompt)]));

const normalizePrompt = (prompt: Prompt): Prompt => ({
  ...prompt,
  promptMetadata: {
    ...prompt.promptMetadata,
    variables: transformLegacyDeclarations(prompt.promptMetadata.variables ?? []),
  },
});
